/**
 * /settings/billing — Pro/Enterprise subscription management
 * ==========================================================
 * Shows the current plan + a danger-zone "Cancel subscription" button.
 *
 * When user clicks Cancel → fetches their 30-day savings number →
 * shows a save-modal:
 *   1. "You've saved $X with CARTIE in the last 30 days"
 *   2. CTA: "Stay for 50% off the next 3 months"
 *   3. Secondary: "Cancel anyway"
 *
 * If user takes stay-offer → POST /api/cancel/accept-stay-offer
 * If user confirms cancel → POST /api/cancel/confirm
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, Loader2, ShieldCheck, AlertTriangle, Sparkles,
  TrendingUp, Check, X, Calendar, DollarSign, MessageSquare,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const BillingSettings = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  // hidden | loading | offer | survey | downsell | stayed | confirmed
  const [modalStep, setModalStep] = useState('hidden');
  const [savings, setSavings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [downsell, setDownsell] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/token-intel-quota/status`, {
          headers: authHeaders(),
        });
        setPlan(data);
      } catch {
        toast.error('Could not load plan');
      } finally { setLoading(false); }
    })();
  }, []);

  const openCancelModal = async () => {
    setModalStep('loading');
    try {
      const { data } = await axios.get(`${API_URL}/cancel/savings-summary`, {
        headers: authHeaders(),
      });
      setSavings(data);
      setModalStep('offer');
    } catch (e) {
      toast.error('Could not load your savings summary');
      setModalStep('hidden');
    }
  };

  const acceptStayOffer = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/cancel/accept-stay-offer`, {}, { headers: authHeaders() });
      setModalStep('stayed');
      toast.success("You're locked in at 50% off · next 3 months");
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply offer');
    } finally { setSubmitting(false); }
  };

  const confirmCancel = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/cancel/confirm`,
        { reason, details },
        { headers: authHeaders() },
      );
      toast(data.message);
      // If we have a downsell offer (e.g. "too_expensive" → $99 budget tier),
      // route to that step instead of straight to confirmed
      if (data.downsell) {
        setDownsell(data.downsell);
        setModalStep('downsell');
      } else {
        setModalStep('confirmed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally { setSubmitting(false); }
  };

  const closeModal = () => {
    setModalStep('hidden');
    setSavings(null);
    setReason(null);
    setDetails('');
    setDownsell(null);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="billing-settings-page">
      <SEOHead title="Billing · CARTIEAI" description="Manage your CARTIE subscription." />

      <nav className="sticky top-0 z-30 backdrop-blur bg-slate-950 text-slate-100/85 border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-200 hover:text-white text-sm" data-testid="billing-nav-home">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold font-mono mb-1.5">/ ACCOUNT</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Billing &amp; subscription</h1>

        {loading ? (
          <div className="mt-6 text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Current plan card */}
            <Card className="mt-8 bg-slate-900 text-slate-100 border-slate-800" data-testid="current-plan-card">
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-300" /> Current plan
                    </CardTitle>
                    <CardDescription className="text-slate-400 mt-1.5">
                      {plan?.pro ? 'All 10 Token Intelligence features unlocked.' : 'Free tier — limited monthly scans.'}
                    </CardDescription>
                  </div>
                  <Badge
                    className={plan?.pro
                      ? 'bg-emerald-500 text-slate-950 font-semibold'
                      : 'bg-slate-700 text-slate-200'}
                    data-testid="plan-badge"
                  >
                    {plan?.pro ? 'PRO · Active' : 'FREE'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-3 gap-3 text-sm">
                <FeatureRow ok={plan?.pro} text="Multi-Model Smart Router™" />
                <FeatureRow ok={plan?.pro} text="Unlimited Context Optimizer" />
                <FeatureRow ok={plan?.pro} text="Unlimited Retry Audit" />
                <FeatureRow ok={plan?.pro} text="Unlimited Drift Alerter" />
                <FeatureRow ok={plan?.pro} text="Unlimited Streaming Diff" />
                <FeatureRow ok={true} text="Public tools always free" />
              </CardContent>
            </Card>

            {!plan?.pro && (
              <Card className="mt-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/5 border-emerald-500/30">
                <CardContent className="p-5 flex flex-col sm:flex-row items-start justify-between gap-3">
                  <div>
                    <p className="text-slate-900 font-semibold">Ready to unlock everything?</p>
                    <p className="text-slate-400 text-sm mt-1">All 10 features. Outcome pricing. Pro starts at $199/mo.</p>
                  </div>
                  <Link to="/pricing">
                    <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="upgrade-cta">
                      Upgrade to Pro
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Danger zone — only for Pro users */}
            {plan?.pro && (
              <Card className="mt-6 bg-slate-900 text-slate-100 border-rose-500/20" data-testid="danger-zone-card">
                <CardHeader>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-400" /> Danger zone
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Cancel your CARTIE Pro subscription. You'll keep full access until the end of your billing period.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={openCancelModal}
                    variant="outline"
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel subscription
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* ─── SAVE MODAL ──────────────────────────────────────────────── */}
      <Dialog open={modalStep !== 'hidden'} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="bg-slate-950 border-emerald-500/30 text-slate-100 max-w-md" data-testid="save-modal">
          {modalStep === 'loading' && (
            <div className="py-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating your savings…
            </div>
          )}

          {modalStep === 'offer' && savings && (
            <>
              <DialogHeader>
                <div className="w-10 h-10 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 border border-emerald-400/30 flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <DialogTitle className="text-2xl text-slate-900 tracking-tight" data-testid="modal-headline">
                  You've saved <span className="text-emerald-300">{fmt(savings.savings_30d_usd)}</span><br />with CARTIE this month.
                </DialogTitle>
                <DialogDescription className="text-slate-400 pt-2">
                  Are you sure you want to cancel? Here's an exclusive offer to keep your team locked in.
                </DialogDescription>
              </DialogHeader>

              {savings.breakdown_top5?.length > 0 && (
                <div className="bg-slate-900 text-slate-100 border border-slate-800 rounded-lg p-3 text-xs space-y-1.5 my-2">
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono mb-1.5">Where your savings came from</div>
                  {savings.breakdown_top5.slice(0, 3).map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-slate-400">
                      <span className="font-mono">{b.feature}</span>
                      <span className="text-emerald-300 font-mono">{fmt(b.savings_usd)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-gradient-to-br from-emerald-500/15 to-cyan-500/5 border border-emerald-500/40 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-emerald-300" />
                  <span className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold font-mono">Exclusive stay offer</span>
                </div>
                <div className="text-slate-900 font-semibold text-lg" data-testid="stay-offer-headline">{savings.stay_offer.headline}</div>
                <div className="text-slate-400 text-sm mt-1">{savings.stay_offer.subline}</div>
              </div>

              <DialogFooter className="flex-col sm:flex-col gap-2 mt-2">
                <Button
                  onClick={acceptStayOffer}
                  disabled={submitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                  data-testid="accept-stay-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Yes, give me 50% off for 3 months
                </Button>
                <Button
                  onClick={() => setModalStep('survey')}
                  disabled={submitting}
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 text-xs"
                  data-testid="advance-to-survey-btn"
                >
                  No thanks, cancel anyway
                </Button>
              </DialogFooter>
            </>
          )}

          {modalStep === 'stayed' && (
            <div className="py-6 text-center" data-testid="modal-stayed">
              <div className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 border border-emerald-400/40 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-white" />
              </div>
              <DialogTitle className="text-xl text-slate-900 tracking-tight">You're staying with us!</DialogTitle>
              <DialogDescription className="text-slate-400 mt-2">
                50% discount locked in for the next 3 months. We'll apply it automatically on your next renewal.
              </DialogDescription>
              <Button onClick={closeModal} className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="stayed-close-btn">
                Awesome, close this
              </Button>
            </div>
          )}

          {modalStep === 'survey' && (
            <div data-testid="modal-survey">
              <DialogHeader>
                <div className="w-10 h-10 rounded-full bg-slate-800 text-slate-100 border border-slate-700 flex items-center justify-center mb-3">
                  <MessageSquare className="w-5 h-5 text-slate-400" />
                </div>
                <DialogTitle className="text-xl text-slate-900 tracking-tight">One quick question before you go</DialogTitle>
                <DialogDescription className="text-slate-400 pt-1">
                  What's the main reason you're cancelling? Helps us build a better product.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 my-3">
                {[
                  { id: 'too_expensive', label: 'Too expensive', icon: DollarSign },
                  { id: 'found_alternative', label: 'Found a better alternative' },
                  { id: 'no_longer_need', label: 'No longer need it' },
                  { id: 'missing_feature', label: "Missing a feature I needed" },
                  { id: 'other', label: 'Other' },
                ].map((r) => {
                  const Icon = r.icon;
                  const selected = reason === r.id;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setReason(r.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all flex items-center gap-2 ${
                        selected
                          ? 'border-emerald-500/50 bg-emerald-500/10 text-white'
                          : 'border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-700 hover:bg-slate-900'
                      }`}
                      data-testid={`survey-reason-${r.id}`}
                    >
                      {Icon ? <Icon className="w-3.5 h-3.5 flex-shrink-0" /> : <span className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className="flex-1">{r.label}</span>
                      {selected && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                    </button>
                  );
                })}
              </div>

              <Textarea
                placeholder="Anything else we should know? (optional)"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={2}
                className="bg-slate-950 border-slate-800 text-slate-200 text-sm"
                data-testid="survey-details-input"
              />

              <DialogFooter className="flex-col sm:flex-col gap-2 mt-4">
                <Button
                  onClick={confirmCancel}
                  disabled={submitting || !reason}
                  variant="outline"
                  className="w-full border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  data-testid="confirm-cancel-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {reason ? 'Confirm cancel' : 'Pick a reason first'}
                </Button>
                <Button
                  onClick={() => setModalStep('offer')}
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-emerald-300 text-xs"
                  data-testid="survey-back-btn"
                >
                  ← Back to stay offer
                </Button>
              </DialogFooter>
            </div>
          )}

          {modalStep === 'downsell' && downsell && (
            <div data-testid="modal-downsell">
              <DialogHeader>
                <div className="w-10 h-10 rounded-full bg-amber-500 shadow-lg shadow-amber-500/40 border border-amber-400/30 flex items-center justify-center mb-3">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <DialogTitle className="text-xl text-slate-900 tracking-tight">
                  Before you go — meet our <span className="text-amber-300">{downsell.headline}</span>
                </DialogTitle>
                <DialogDescription className="text-slate-400 pt-1">
                  {downsell.subline}
                </DialogDescription>
              </DialogHeader>

              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-lg p-4 my-3 text-center">
                <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold font-mono">Budget tier</div>
                <div className="text-4xl font-bold text-slate-900 mt-2 tabular-nums">${downsell.price_per_month_usd}<span className="text-base font-medium text-slate-400">/mo</span></div>
                <div className="text-xs text-slate-400 mt-1">Cancel anytime · same features</div>
              </div>

              <DialogFooter className="flex-col sm:flex-col gap-2">
                <Button
                  onClick={() => { window.location.href = '/pricing?tier=budget'; }}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                  data-testid="downsell-accept-btn"
                >
                  Switch to Budget — keep my account
                </Button>
                <Button
                  onClick={() => setModalStep('confirmed')}
                  variant="ghost"
                  className="w-full text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 text-xs"
                  data-testid="downsell-decline-btn"
                >
                  No thanks, still cancel
                </Button>
              </DialogFooter>
            </div>
          )}

          {modalStep === 'confirmed' && (
            <div className="py-6 text-center" data-testid="modal-cancelled">
              <div className="w-12 h-12 rounded-full bg-slate-800 text-slate-100 border border-slate-700 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-slate-400" />
              </div>
              <DialogTitle className="text-xl text-slate-900 tracking-tight">Subscription cancelled</DialogTitle>
              <DialogDescription className="text-slate-400 mt-2">
                You'll keep full access until the end of your billing period. We're sorry to see you go.
              </DialogDescription>
              <Button onClick={closeModal} variant="outline" className="mt-5 border-slate-700 text-slate-700" data-testid="cancelled-close-btn">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FeatureRow = ({ ok, text }) => (
  <div className="flex items-center gap-2 text-slate-600 text-sm">
    {ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-slate-700" />}
    {text}
  </div>
);

export default BillingSettings;
